import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { intersection, includes } from 'lodash';
import { FormHelpText, Type, TypeList, TypeListItem } from '@spotify-internal/encore-creator-web';
import colors from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import { PODCAST_SUPPORT_EMAIL } from 'lib/constants';

const MailLink = styled.a`
  color: ${colors.azure};
  font-weight: 600;
  text-decoration: underline;
  &:hover {
    color: black;
  }
`;

const GENERAL_ERRORS = {
  DOWNLOAD_FAILURE: 'DOWNLOAD_FAILURE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  NEEDS_APPROVAL: 'NEEDS_APPROVAL',
  INVALID_XML: 'INVALID_XML',
  VIDEO_PODCAST: 'VIDEO_PODCAST',
  DIFFERENT_SHOW: 'DIFFERENT_SHOW',
};

const MISSING_ERRORS = {
  MISSING_IMAGE: 'MISSING_IMAGE',
  MISSING_DESCRIPTION: 'MISSING_DESCRIPTION',
  MISSING_TITLE: 'MISSING_TITLE',
  MISSING_EPISODES: 'MISSING_EPISODES',
  MISSING_AUTHOR: 'MISSING_AUTHOR',
  MISSING_GUID: 'MISSING_GUID',
  MISSING_EMAIL: 'MISSING_EMAIL',
};

const errorMessages = {
  [GENERAL_ERRORS.DOWNLOAD_FAILURE]:
    'That URL doesn’t look right. Make sure you typed it correctly.',
  [GENERAL_ERRORS.UNKNOWN_ERROR]: 'Something has gone wrong. Please try again',
  [GENERAL_ERRORS.NEEDS_APPROVAL]: (
    <Fragment>
      {`You’re a VIP! To get this podcast on Spotify, you need to `}
      <MailLink href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>contact our team</MailLink>
      {` for help.`}
    </Fragment>
  ),
  [GENERAL_ERRORS.INVALID_XML]: 'Your RSS feed failed to load. Make sure you added the right link.',
  [GENERAL_ERRORS.VIDEO_PODCAST]: (
    <Fragment>
      {`We’re unable to accept podcasts with videos. `}
      <a href="/faq#can-i-have-video-episodes-in-my-rss-feed">Learn more</a>
    </Fragment>
  ),
  [GENERAL_ERRORS.DIFFERENT_SHOW]: 'The link to the RSS feed is for a different podcast.',
  [MISSING_ERRORS.MISSING_IMAGE]: 'Cover art',
  [MISSING_ERRORS.MISSING_DESCRIPTION]: 'Description',
  [MISSING_ERRORS.MISSING_TITLE]: 'Title',
  [MISSING_ERRORS.MISSING_EPISODES]: 'Episodes',
  [MISSING_ERRORS.MISSING_AUTHOR]: 'Author',
  [MISSING_ERRORS.MISSING_EMAIL]: 'Email address',
  [MISSING_ERRORS.MISSING_GUID]: (
    <Fragment>
      {`Your podcast needs a globally unique identifier (GUID). `}
      <a href="/faq#whats-a-globally-unique-identifier-guid">Learn more</a>
    </Fragment>
  ),
};

const ErrorContainer = styled.div`
  margin-top: 16px;
`;

const StyledTypeListItem = styled(TypeListItem)`
  font-size: 14px;
`;

export class PodcastUpdateUrlErrorFeedback extends PureComponent {
  static propTypes = {
    errors: PropTypes.arrayOf(PropTypes.string),
  };

  isGeneralError = () => {
    return intersection(this.props.errors, Object.values(GENERAL_ERRORS)).length > 0;
  };

  generalErrorTemplate = errors => (
    <Fragment>
      {errors.map(error => (
        <Type
          as="p"
          data-qa="download-failure"
          color={error === GENERAL_ERRORS.NEEDS_APPROVAL ? 'azure' : 'failure'}
          variant={Type.sm}
          key={error}
        >
          {errorMessages[GENERAL_ERRORS[error]]}
        </Type>
      ))}
      {includes(errors, GENERAL_ERRORS.INVALID_XML) && (
        <FormHelpText>
          Need help? Try looking in the Help section of your podcast aggregator/host.
        </FormHelpText>
      )}
    </Fragment>
  );

  missingInfoErrorTemplate = errors => (
    <Fragment>
      <FormHelpText error>
        Your podcast RSS feed is missing some things:
        <TypeList.ul data-qa="error-list">
          {errors.map(errorType => (
            <StyledTypeListItem condensed key={errorType}>
              {errorMessages[errorType]}
            </StyledTypeListItem>
          ))}
        </TypeList.ul>
      </FormHelpText>
      <FormHelpText>
        Need help? Try looking in the Help section of your podcast aggregator/host.
      </FormHelpText>
    </Fragment>
  );

  render() {
    const errorMessage = this.isGeneralError()
      ? this.generalErrorTemplate(this.props.errors)
      : this.missingInfoErrorTemplate(this.props.errors);
    return <ErrorContainer>{errorMessage}</ErrorContainer>;
  }
}
